import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import CreateTestPlanModal from '../test-plans/CreateTestPlanModal';
import RunTestPlanModal from '../test-plans/RunTestPlanModal';
import ScheduleTestPlanModal from '../test-plans/ScheduleTestPlanModal';
import LinkTestSuitesModal from '../test-plans/LinkTestSuitesModal';
import PlansEntityWorkspace from './PlansEntityWorkspace';
import {
  Breadcrumbs,
  ConfirmDialog,
  DataTable,
  ErrorState,
  FilterBar,
  LoadingState,
  MetricCard,
  PageScaffold,
  PriorityBadge,
  RowActions,
  SearchInput,
  StatusBadge,
  Tabs,
} from '../ui';
import { formatDate } from '../../utils/dateTimeFormat';
import '../../styles/rt2-plans-entity.css';

const ENTITY_TABS = new Set(['releases', 'sprints', 'epics']);
const TAB_IDS = ['all', 'releases', 'sprints', 'epics'];

const normalizeTab = (value) => {
  if (!value) return 'all';
  const raw = String(value).toLowerCase();
  if (raw === 'release') return 'releases';
  if (raw === 'sprint') return 'sprints';
  if (raw === 'epic') return 'epics';
  return TAB_IDS.includes(raw) ? raw : 'all';
};

/**
 * BUILD · Test Plans
 * All → flat plan table (no entity selection)
 * Release / Sprint / Epic → full-width workspace with searchable entity selector
 */
const PlansWorkspace = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;
  const projectName = selectedProject?.name || 'Project';

  const [tab, setTab] = useState(() => normalizeTab(searchParams.get('tab')));
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [runPlan, setRunPlan] = useState(null);
  const [schedulePlan, setSchedulePlan] = useState(null);
  const [linkPlan, setLinkPlan] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [members, setMembers] = useState([]);

  const isEntityTab = ENTITY_TABS.has(tab);

  useEffect(() => {
    const fromQuery = normalizeTab(searchParams.get('tab'));
    if (fromQuery !== tab) setTab(fromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeTab = (id) => {
    const next = normalizeTab(id);
    setTab(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true }
    );
  };

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const loadMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const [envRes, memRes] = await Promise.all([
        axiosInstance.get(`/user_profile/list_environments?project_id=${projectId}`),
        axiosInstance.get(`/projects/list_project_members?project_id=${projectId}`),
      ]);
      setEnvironments(envRes?.data || envRes || []);
      setMembers(memRes?.data || memRes?.members || memRes || []);
    } catch (err) {
      console.error(err);
    }
  }, [projectId]);

  const load = useCallback(async () => {
    if (!projectId) {
      setPlans([]);
      setLoading(false);
      setError('Select a project to manage plans.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/test_plan/test-plans/all_type', {
        params: { project_id: projectId },
      });
      const raw = response?.test_plans || [];
      const transformed = (Array.isArray(raw) ? raw : []).map((plan) => ({
        test_plan_id: plan.test_plan_id,
        id: plan.test_plan_id,
        name: plan.name,
        description: plan.description,
        status: plan.status || 'draft',
        priority: plan.priority || 'medium',
        assignee: plan.assignee || 'Unassigned',
        assignee_id: plan.assignee_id || null,
        created_at: plan.created_at,
        updated_at: plan.updated_at,
        testCases: plan?.test_case_count || 0,
        testSuites: plan.linked_suites
          ? plan.linked_suites.map((suite) => ({
              suite_id: suite.suite_id,
              suite_name: suite.suite_name,
            }))
          : [],
        projectId: plan.project_id,
        typeId: plan.type_id,
        referenceId: plan.reference_id,
        environment_setting_id: plan.environment_setting_id ?? null,
      }));
      setPlans(transformed);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!isEntityTab) load();
  }, [load, isEntityTab]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const handleExport = async () => {
    if (!projectId) return;
    setExporting(true);
    try {
      const blobData = await axiosInstance.get('/test_plan/export_test_plans', {
        params: { project_id: projectId },
        responseType: 'blob',
      });
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-plans-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter(
      (p) =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.status || '').toLowerCase().includes(q) ||
        String(p.assignee || '').toLowerCase().includes(q) ||
        String(p.description || '').toLowerCase().includes(q)
    );
  }, [plans, query]);

  const openPlan = (plan) => {
    navigateTo(`test-plans/${plan.test_plan_id || plan.id}`);
  };

  const handleCreateSave = async (payload) => {
    try {
      await axiosInstance.post('/test_plan/test-plans', {
        ...payload,
        project_id: projectId,
      });
      setCreateOpen(false);
      await load();
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deletePlan) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/test_plan/delete/${deletePlan.test_plan_id || deletePlan.id}`);
      setDeletePlan(null);
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div>
          <button
            type="button"
            className="rt2-link"
            onClick={(e) => {
              e.stopPropagation();
              openPlan(row);
            }}
          >
            {row.name}
          </button>
          {row.description && (
            <div className="rt2-muted" style={{ marginTop: 2 }}>
              {String(row.description).slice(0, 72)}
              {String(row.description).length > 72 ? '…' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: 'suites',
      header: 'Suites',
      render: (row) => row.testSuites?.length ?? 0,
    },
    {
      key: 'cases',
      header: 'Cases',
      render: (row) => row.testCases ?? 0,
    },
    {
      key: 'assignee',
      header: 'Owner',
      render: (row) => row.assignee || '—',
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (row) => formatDate(row.updated_at, '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 140,
      render: (row) => (
        <RowActions
          primary={
            <button
              type="button"
              className="rt-btn rt-btn--secondary rt-btn--sm"
              onClick={() => setRunPlan(row)}
              title="Run plan"
              aria-label={`Run ${row.name || row.test_plan_name}`}
            >
              Run
            </button>
          }
          menuItems={[
            { id: 'view', label: 'View', onClick: () => openPlan(row) },
            { id: 'link', label: 'Link suites', onClick: () => setLinkPlan(row) },
            { id: 'schedule', label: 'Schedule', onClick: () => setSchedulePlan(row) },
            {
              id: 'delete',
              label: 'Delete',
              danger: true,
              dividerBefore: true,
              onClick: () => setDeletePlan(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageScaffold
      title="Test Plans"
      description="Plan and schedule test executions across releases, sprints, and epics."
      breadcrumbs={<Breadcrumbs items={[{ label: 'Build' }, { label: 'Test Plans' }]} />}
      actions={
        !isEntityTab ? (
          <>
            <button
              type="button"
              className="rt-btn rt-btn--secondary"
              onClick={handleExport}
              disabled={!projectId || exporting}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
            <button type="button" className="rt-btn rt-btn--secondary" onClick={load} disabled={!projectId}>
              Refresh
            </button>
            <button
              type="button"
              className="rt-btn rt-btn--primary"
              disabled={!projectId}
              onClick={() => setCreateOpen(true)}
            >
              + Create Plan
            </button>
          </>
        ) : null
      }
    >
      <div className="rt-plans-tabs">
        <Tabs
          activeId={tab}
          onChange={changeTab}
          tabs={[
            { id: 'all', label: 'All' },
            { id: 'releases', label: 'Release' },
            { id: 'sprints', label: 'Sprint' },
            { id: 'epics', label: 'Epic' },
          ]}
        />
      </div>

      {isEntityTab ? (
        <PlansEntityWorkspace tab={tab} projectId={projectId} projectName={projectName} />
      ) : (
        <>
          <div className="rt-metric-grid rt-plans-metrics">
            <MetricCard label="Plans" value={loading ? '…' : plans.length} tone="primary" />
            <MetricCard
              label="Active"
              tone="success"
              value={
                loading
                  ? '…'
                  : plans.filter((p) => String(p.status).toLowerCase() === 'active').length
              }
            />
            <MetricCard
              label="Draft"
              tone="warning"
              value={
                loading
                  ? '…'
                  : plans.filter((p) => String(p.status).toLowerCase() === 'draft').length
              }
            />
            <MetricCard
              label="Suites linked"
              tone="accent"
              value={
                loading
                  ? '…'
                  : plans.reduce((n, p) => n + (p.testSuites?.length || 0), 0)
              }
            />
          </div>

          <FilterBar>
            <SearchInput value={query} onChange={setQuery} placeholder="Search plans…" />
          </FilterBar>

          {loading && <LoadingState label="Loading plans…" />}
          {!loading && error && !plans.length && (
            <ErrorState
              title="Unable to load plans"
              description={
                error?.includes('Select a project')
                  ? 'Please select a project before continuing.'
                  : error
              }
              onRetry={load}
            />
          )}
          {!loading && (
            <DataTable
              columns={columns}
              rows={filtered}
              rowKey="test_plan_id"
              onRowClick={openPlan}
              emptyTitle="No test plans"
              emptyDescription="Create a plan, link suites, then run from Executions."
            />
          )}
        </>
      )}

      {createOpen && (
        <CreateTestPlanModal
          onClose={() => setCreateOpen(false)}
          onSave={handleCreateSave}
          selectedEntity={null}
          activeTab="releases"
          projectName={projectName}
          projectId={projectId}
          initialEnvironments={Array.isArray(environments) ? environments : []}
          initialMembers={Array.isArray(members) ? members : []}
        />
      )}

      {runPlan && (
        <RunTestPlanModal
          isOpen={Boolean(runPlan)}
          onClose={() => setRunPlan(null)}
          testPlanIds={[runPlan.test_plan_id || runPlan.id]}
          testPlanNames={[runPlan.name]}
          projectId={projectId}
          projectName={projectName}
          environmentSettingId={runPlan.environment_setting_id}
        />
      )}

      {schedulePlan && (
        <ScheduleTestPlanModal
          isOpen={Boolean(schedulePlan)}
          onClose={() => setSchedulePlan(null)}
          testPlanList={plans}
          initialPlan={schedulePlan}
          projectId={projectId}
          onFeedback={() => setSchedulePlan(null)}
        />
      )}

      {linkPlan && (
        <LinkTestSuitesModal
          isOpen={Boolean(linkPlan)}
          onClose={() => setLinkPlan(null)}
          testPlan={linkPlan}
          projectId={projectId}
          onSuccess={() => {
            setLinkPlan(null);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deletePlan)}
        title="Delete plan?"
        body={`Delete "${deletePlan?.name}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setDeletePlan(null)}
        onConfirm={confirmDelete}
      />
    </PageScaffold>
  );
};

export default PlansWorkspace;
